import type { AdminIdentity, ApiError, DeletionLogSummary, PublicVideo, VideoSummary } from '@logica/shared';

const apiBase = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, '') ?? '';
let csrfToken: string | null = null;

async function parseResponse<T>(response: Response): Promise<T> {
  if (response.status === 204) return undefined as T;
  const payload = (await response.json().catch(() => ({ error: 'The server returned an invalid response.' }))) as T | ApiError;
  if (!response.ok) throw new Error('error' in (payload as object) ? (payload as ApiError).error : 'The request failed.');
  return payload as T;
}

export async function getCsrfToken(force = false): Promise<string> {
  if (csrfToken && !force) return csrfToken;
  const response = await fetch(`${apiBase}/api/auth/csrf`, { credentials: 'include', cache: 'no-store' });
  const payload = await parseResponse<{ csrfToken: string }>(response);
  csrfToken = payload.csrfToken;
  return csrfToken;
}

export async function api<T>(path: string, options: RequestInit = {}): Promise<T> {
  const method = options.method?.toUpperCase() ?? 'GET';
  const headers = new Headers(options.headers);
  if (options.body && !(options.body instanceof FormData)) headers.set('Content-Type', 'application/json');
  if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) headers.set('x-csrf-token', await getCsrfToken());
  const response = await fetch(`${apiBase}${path}`, { ...options, headers, credentials: 'include', cache: 'no-store' });
  if (response.status === 403 && !['GET', 'HEAD', 'OPTIONS'].includes(method)) {
    headers.set('x-csrf-token', await getCsrfToken(true));
    return parseResponse<T>(await fetch(`${apiBase}${path}`, { ...options, headers, credentials: 'include', cache: 'no-store' }));
  }
  return parseResponse<T>(response);
}

export const authApi = {
  me: () => api<{ admin: AdminIdentity | null }>('/api/auth/me'),
  login: async (email: string, password: string) => {
    const result = await api<{ admin: AdminIdentity; csrfToken: string }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password })
    });
    csrfToken = result.csrfToken;
    return result.admin;
  },
  logout: () => api<void>('/api/auth/logout', { method: 'POST' })
};

export const videosApi = {
  list: () => api<{ videos: VideoSummary[]; deletionLogs: DeletionLogSummary[] }>('/api/admin/videos'),
  show: (publicId: string) => api<{ video: VideoSummary }>(`/api/admin/videos/${publicId}`),
  update: (publicId: string, input: Pick<VideoSummary, 'title' | 'eventName' | 'eventAt' | 'expiresAt' | 'deleteAt'>) =>
    api<{ video: VideoSummary }>(`/api/admin/videos/${publicId}`, {
      method: 'PATCH',
      body: JSON.stringify(input)
    }),
  publicStatus: (publicId: string) => api<PublicVideo>(`/api/videos/${publicId}/status`),
  retry: (publicId: string) => api<{ video: VideoSummary }>(`/api/admin/videos/${publicId}/retry`, { method: 'POST' }),
  expire: (publicId: string) => api<{ video: VideoSummary }>(`/api/admin/videos/${publicId}/expire`, { method: 'POST' }),
  delete: (publicId: string, confirmationTitle: string) =>
    api<{ video: VideoSummary }>(`/api/admin/videos/${publicId}`, {
      method: 'DELETE',
      body: JSON.stringify({ confirmationTitle, confirmation: 'DELETE' })
    }),
  event: (publicId: string, event: 'play' | 'pause' | 'ended' | 'error') =>
    fetch(`${apiBase}/api/videos/${publicId}/events`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event }),
      keepalive: true
    }).catch(() => undefined)
};

type ChunkedUploadSession = {
  uploadId: string;
  fileName: string;
  fileSize: number;
  chunkSize: number;
  totalChunks: number;
};

class ChunkRequestError extends Error {
  constructor(message: string, readonly status = 0) {
    super(message);
  }
}

function uploadResponseMessage(request: XMLHttpRequest): string {
  try {
    const payload = JSON.parse(request.responseText || '{}') as { error?: string };
    if (payload.error) return payload.error;
  } catch {
    // Reverse proxies can return HTML error pages, so use the HTTP status below.
  }
  if (request.status === 413) return 'The upload gateway rejected a file part as too large.';
  if (request.status === 401) return 'Your administrator session expired. Sign in and try again.';
  return request.status ? `The upload failed with HTTP ${request.status}.` : 'The upload connection was interrupted.';
}

function sendUploadChunk(
  uploadId: string,
  chunkIndex: number,
  chunk: Blob,
  checksum: string,
  csrf: string,
  signal: AbortSignal | undefined,
  onChunkProgress: (loaded: number) => void
): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    const abort = () => request.abort();
    const finish = () => signal?.removeEventListener('abort', abort);
    request.open('PUT', `${apiBase}/api/admin/videos/uploads/${uploadId}/chunks/${chunkIndex}`);
    request.withCredentials = true;
    request.timeout = 10 * 60_000;
    request.setRequestHeader('Content-Type', 'application/octet-stream');
    request.setRequestHeader('x-chunk-sha256', checksum);
    request.setRequestHeader('x-csrf-token', csrf);
    request.upload.addEventListener('progress', (event) => onChunkProgress(event.loaded));
    request.addEventListener('load', () => {
      finish();
      if (request.status >= 200 && request.status < 300) resolve();
      else reject(new ChunkRequestError(uploadResponseMessage(request), request.status));
    });
    request.addEventListener('error', () => {
      finish();
      reject(new ChunkRequestError('The upload connection was interrupted.'));
    });
    request.addEventListener('timeout', () => {
      finish();
      reject(new ChunkRequestError('A video part took too long to upload.'));
    });
    request.addEventListener('abort', () => {
      finish();
      reject(new ChunkRequestError('The upload was cancelled.'));
    });
    signal?.addEventListener('abort', abort, { once: true });
    if (signal?.aborted) {
      request.abort();
      return;
    }
    request.send(chunk);
  });
}

async function sha256Hex(blob: Blob): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', await blob.arrayBuffer());
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
}

function waitBeforeRetry(delayMs: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => {
      signal?.removeEventListener('abort', abort);
      resolve();
    }, delayMs);
    const abort = () => {
      window.clearTimeout(timer);
      reject(new Error('The upload was cancelled.'));
    };
    signal?.addEventListener('abort', abort, { once: true });
  });
}

export async function uploadVideo(
  form: FormData,
  onProgress: (progress: number) => void,
  signal?: AbortSignal
): Promise<VideoSummary> {
  const video = form.get('video');
  if (!(video instanceof File) || video.size === 0) throw new Error('Choose a video to upload.');
  const poster = form.get('poster');
  if (poster instanceof File && poster.size > 10 * 1024 * 1024) throw new Error('Poster images may be at most 10 MB.');

  let uploadId: string | null = null;
  try {
    const session = await api<ChunkedUploadSession>('/api/admin/videos/uploads', {
      method: 'POST',
      body: JSON.stringify({ fileName: video.name, fileSize: video.size, mimeType: video.type || 'application/octet-stream' }),
      ...(signal ? { signal } : {})
    });
    uploadId = session.uploadId;
    let highestProgress = 0;
    let csrf = await getCsrfToken();
    for (let chunkIndex = 0; chunkIndex < session.totalChunks; chunkIndex += 1) {
      const start = chunkIndex * session.chunkSize;
      const end = Math.min(start + session.chunkSize, video.size);
      const chunk = video.slice(start, end, 'application/octet-stream');
      const checksum = await sha256Hex(chunk);
      let uploaded = false;
      for (let attempt = 0; attempt < 3 && !uploaded; attempt += 1) {
        try {
          await sendUploadChunk(uploadId, chunkIndex, chunk, checksum, csrf, signal, (loaded) => {
            const progress = Math.min(99, Math.round(((start + loaded) / video.size) * 100));
            highestProgress = Math.max(highestProgress, progress);
            onProgress(highestProgress);
          });
          uploaded = true;
        } catch (reason) {
          if (signal?.aborted) throw new Error('The upload was cancelled.');
          const chunkError = reason instanceof ChunkRequestError ? reason : new ChunkRequestError('The upload connection was interrupted.');
          if (chunkError.status === 403 && attempt < 2) {
            csrf = await getCsrfToken(true);
            continue;
          }
          const retryable = chunkError.status === 0 || chunkError.status === 408 || chunkError.status === 429 || chunkError.status >= 500;
          if (!retryable || attempt === 2) throw chunkError;
          await waitBeforeRetry(1000 * 2 ** attempt, signal);
        }
      }
      highestProgress = Math.max(highestProgress, Math.min(99, Math.round((end / video.size) * 100)));
      onProgress(highestProgress);
    }

    onProgress(100);
    const completionForm = new FormData();
    for (const [key, value] of form.entries()) {
      if (key !== 'video') completionForm.append(key, value);
    }
    const completed = await api<{ video: VideoSummary }>(`/api/admin/videos/uploads/${uploadId}/complete`, {
      method: 'POST',
      body: completionForm,
      ...(signal ? { signal } : {})
    });
    uploadId = null;
    return completed.video;
  } catch (error) {
    if (uploadId) {
      await api<void>(`/api/admin/videos/uploads/${uploadId}`, { method: 'DELETE' }).catch(() => undefined);
    }
    if (signal?.aborted) throw new Error('The upload was cancelled.');
    throw error;
  }
}
